'use client';

import React from 'react';
import { cn } from '../lib/utils';
import { Collapsible } from './collapsible';
import {
  usePageLayoutPreferences,
  type PageLayoutPreferences,
  type PagePreferenceScope,
} from '../hooks/use-page-layout-preferences';

export interface CollapsibleSectionProps {
  title: string;
  sectionId: string;
  scope: PagePreferenceScope;
  summary?: React.ReactNode;
  count?: number | null;
  defaultExpanded?: boolean;
  /** Map existing app storage hooks here; ready=false withholds optional content. */
  preferences?: PageLayoutPreferences;
  children: React.ReactNode;
  className?: string;
}

export function CollapsibleSection({
  title,
  sectionId,
  scope,
  summary,
  count,
  defaultExpanded = false,
  preferences,
  children,
  className,
}: CollapsibleSectionProps) {
  const fallback = usePageLayoutPreferences(scope, 'expanded', !preferences);
  const store = preferences ?? fallback;
  const expanded = store.ready && (store.values[sectionId] ?? defaultExpanded);
  return (
    <Collapsible
      label={title}
      summary={summary}
      count={count}
      heading
      open={expanded}
      onOpenChange={(next) => store.set(sectionId, next)}
      disabled={!store.ready}
      aria-busy={!store.ready}
      keepMounted={false}
      className={cn('border-y border-border', className)}
    >
      {children}
    </Collapsible>
  );
}
